import { IRenderingCommonContext } from "@commonRendering/types";
import { ICheckoutWidgetData, ICheckoutWidgetShortData, StoreCheckoutWidgetData } from "src/typings/checkout-widget-data";
import { Base64Util } from "src/utils/base64-util";
import { WidgetRendererBase } from "../widget-renderer-base";
import { CheckoutWidgetScriptRenderer } from "./checkout-widget-script-renderer";
import { StoreCheckoutWidgetStyleRenderer } from "./checkout-widget-style-renderer";
import { SiteDesignStylesService } from "src/services/site-design-styles.service";

export class CheckoutWidgetRenderer extends WidgetRendererBase<ICheckoutWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-checkout";
    private readonly scriptRenderer: CheckoutWidgetScriptRenderer;
    private readonly styleRenderer: StoreCheckoutWidgetStyleRenderer;
    private readonly siteDesignStyleService: SiteDesignStylesService;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new CheckoutWidgetScriptRenderer();
        this.styleRenderer = new StoreCheckoutWidgetStyleRenderer(this.styleService);
        this.siteDesignStyleService = SiteDesignStylesService.createByDoc(renderingCommonContext.renderedDoc);
    }

    protected override createNewInternal(): HTMLElement {
        return this.createWidgetElement("checkoutWidget");
    }

    protected override renderInternal(element: HTMLElement, data: ICheckoutWidgetData): HTMLElement {
        this.setAttributes(element, data);
        this.renderStyles(element, data);
        this.renderScripts(element, data);

        return element;
    }

    private setAttributes(element: HTMLElement, data: ICheckoutWidgetData): void {
        if (this.isEditorRendering) {
            element.dataset.draft = "true";
        } else if (this.isPreviewRendering) {
            element.dataset.preview = "true";
        }

        const shortData = this.getShortData(data);
        const json = Base64Util.serialize(shortData) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);

        if (data.primaryElement) {
            element.dataset.primaryElement = "true";
        } else {
            delete element.dataset.primaryElement;
        }
    }

    private getShortData(data: ICheckoutWidgetData): ICheckoutWidgetShortData {
        return {
            testMode: data.testMode,
            buttonTheme: data.style?.button.theme ?? StoreCheckoutWidgetData.defaultStyle.button.theme
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override renderStyles(element: HTMLElement, data: ICheckoutWidgetData): void {
        if (!data.style) {
            return;
        }

        this.animationRenderer.setAnimationStyles(element, data.style.animation);
        this.styleRenderer.render(element, data.style);
    }

    private renderScripts(element: HTMLElement, data: ICheckoutWidgetData): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);

        const previousData = this.getData(element);
        if (!StoreCheckoutWidgetData.localScriptRerenderNeeded(previousData, data)) {
            this.scriptRenderer.renderLocal(element);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [];
    }

}